import React, { useState } from 'react';

import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { useGetLensColumnValues } from '~/hooks/apis/reporting/useLensColumnValues';

import type { LensFilterValues } from '~/types/leLens';

import DeclareNowBanner from './DeclareNowBanner';
import type { PanelState } from './usePanelState';

export type WhereChipsProps = {
  state: PanelState;
  /** The Lens the card belongs to; without it the picker cannot list values and falls back to typing. */
  lensId?: string;
  /** The board's active filter values, so the listed values match the period on screen. */
  filterValues?: LensFilterValues;
  /** The Declare-now banner's own action, shared with Data's source details. */
  onDeclareNow?: () => void;
  declaring?: boolean;
};

/** What the Values field says beneath itself while the list loads, arrives, is cut short, or fails. */
export function valuesHelperText(args: {
  column: string;
  loading: boolean;
  error: boolean;
  unavailable?: string;
  truncated?: boolean;
  count: number;
}): string {
  if (!args.column) {
    return 'Choose a field first.';
  }
  if (args.loading) {
    return 'Listing the values in this dataset…';
  }
  if (args.error) {
    return 'Values could not be listed. Type them exactly instead.';
  }
  if (args.unavailable) {
    return `${args.unavailable} Type values exactly instead.`;
  }
  if (args.truncated) {
    return `Showing the first ${args.count} values. Type any others exactly.`;
  }
  if (args.count === 0) {
    return 'No values in the current period. Type them exactly instead.';
  }
  return 'Pick from the list, or type a value and press Enter.';
}

export default function WhereChips({ declaring, filterValues = {}, lensId, onDeclareNow, state }: WhereChipsProps) {
  const where = state.draft.view.where ?? [];
  const [adding, setAdding] = useState(false);
  const [column, setColumn] = useState('');
  const [operator, setOperator] = useState('in');
  const [values, setValues] = useState<Array<string>>([]);
  const fields = state.fields.filter((field) => field.kind === 'dimension');
  const columnValues = useGetLensColumnValues({
    lensId,
    datasetId: state.draft.datasetId,
    column: adding ? column : undefined,
    filters: filterValues,
  });
  const options = columnValues.data?.values ?? [];
  const helper = valuesHelperText({
    column,
    loading: columnValues.isLoading && Boolean(column),
    error: columnValues.isError,
    unavailable: columnValues.data?.unavailable,
    truncated: columnValues.data?.truncated,
    count: options.length,
  });

  const openForm = (nextColumn: string, nextOperator: string, nextValues: Array<string>) => {
    setColumn(nextColumn);
    setOperator(nextOperator);
    setValues(nextValues);
    setAdding(true);
  };

  return (
    <Stack aria-label="Chart filters" spacing={1}>
      {state.isUndeclared && <DeclareNowBanner declaring={declaring} onDeclareNow={onDeclareNow} />}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {where.map((clause) => {
          const clauseValues = clause.in ?? clause.notIn ?? [];
          const field = state.fields.find((item) => item.name === clause.column);
          return (
            <Chip
              key={clause.column}
              label={`${field?.label ?? clause.column} ${clause.notIn ? 'excludes' : 'includes'} ${clauseValues.join(', ')}`}
              onClick={() => openForm(clause.column, clause.notIn ? 'notIn' : 'in', clauseValues)}
              onDelete={() => state.removeWhere(clause.column)}
              size="small"
              variant="outlined"
            />
          );
        })}
      </Box>
      {adding ? (
        <Stack spacing={1}>
          <TextField
            select
            fullWidth
            label="Field"
            size="small"
            value={column}
            onChange={(event) => {
              setColumn(event.target.value);
              setValues([]);
            }}
          >
            {fields.map((field) => (
              <MenuItem key={field.name} value={field.name}>
                {field.label ?? field.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            fullWidth
            label="Condition"
            size="small"
            value={operator}
            onChange={(event) => setOperator(event.target.value)}
          >
            <MenuItem value="in">Include values</MenuItem>
            <MenuItem value="notIn">Exclude values</MenuItem>
          </TextField>
          <Autocomplete<string, true, false, true>
            disableCloseOnSelect
            disabled={!column}
            freeSolo
            multiple
            filterSelectedOptions={false}
            loading={columnValues.isLoading && Boolean(column)}
            loadingText="Listing values…"
            noOptionsText={columnValues.data?.unavailable ? 'Type a value and press Enter' : 'No matching values'}
            onChange={(_event, next) => setValues([...new Set(next.map((item) => item.trim()).filter(Boolean))])}
            options={options}
            renderInput={(params) => (
              <TextField
                {...params}
                helperText={helper}
                label="Values"
                size="small"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {columnValues.isLoading && column ? <CircularProgress color="inherit" size={16} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(props, option, { selected }) => (
              <li {...props} key={option}>
                {selected ? (
                  <CheckBoxIcon fontSize="small" sx={{ color: 'primary.main', mr: 1 }} />
                ) : (
                  <CheckBoxOutlineBlankIcon fontSize="small" sx={{ color: 'text.secondary', mr: 1 }} />
                )}
                {option}
              </li>
            )}
            renderTags={(tagValues, getTagProps) =>
              tagValues.map((option, index) => (
                <Chip {...getTagProps({ index })} key={option} label={option} size="small" />
              ))
            }
            size="small"
            value={values}
          />
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="contained"
              disabled={!column || values.length === 0}
              onClick={() => {
                state.setWhere(column, values, operator === 'notIn');
                setAdding(false);
                setValues([]);
              }}
            >
              Apply
            </Button>
            <Button size="small" onClick={() => setAdding(false)}>
              Cancel
            </Button>
          </Stack>
        </Stack>
      ) : (
        <>
          {where.length === 0 && (
            <Typography color="text.secondary" variant="caption">
              No chart filters yet.
            </Typography>
          )}
          <Button
            size="small"
            variant="outlined"
            disabled={!fields.length}
            onClick={() => openForm('', 'in', [])}
            sx={{ alignSelf: 'flex-start' }}
          >
            Add filter
          </Button>
        </>
      )}
    </Stack>
  );
}
